import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Info } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "", document_number: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cName = (c) => (lang === "it" ? c.name_it : c.name_en);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country) { toast.error(t("auth.country")); return; }
    setLoading(true);
    try {
      await register(form);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/auth/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("auth.registerNote"));
      nav("/profile");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Card className="p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center mb-3"><UserPlus className="h-6 w-6 text-primary-foreground" /></div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{t("auth.registerTitle")}</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="name">{t("auth.name")}</Label><Input id="name" data-testid="reg-name" value={form.name} onChange={set("name")} required className="mt-1.5" /></div>
          <div><Label htmlFor="email">{t("auth.email")}</Label><Input id="email" data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" /></div>
          <div><Label htmlFor="password">{t("auth.password")}</Label><Input id="password" data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5" /></div>
          <div>
            <Label>{t("auth.country")}</Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger data-testid="reg-country" className="mt-1.5"><SelectValue placeholder={t("auth.country")} /></SelectTrigger>
              <SelectContent>{countries.map((c) => <SelectItem key={c.code} value={c.code}>{cName(c)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="doc">{t("auth.docNumber")}</Label><Input id="doc" data-testid="reg-docnum" value={form.document_number} onChange={set("document_number")} required className="mt-1.5" /></div>
          <div>
            <Label htmlFor="file">{t("auth.doc")}</Label>
            <Input id="file" data-testid="reg-docfile" type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5" />
          </div>
          <div className="flex gap-2 items-start text-xs text-muted-foreground bg-secondary rounded-md p-3"><Info className="h-4 w-4 shrink-0 mt-0.5" /><span>{t("auth.registerNote")}</span></div>
          <Button data-testid="reg-submit" type="submit" className="w-full" disabled={loading}>{loading ? t("common.loading") : t("nav.register")}</Button>
        </form>
        <p className="mt-5 text-sm text-center text-muted-foreground">
          {t("auth.haveAccount")} <Link to="/login" data-testid="to-login" className="text-primary font-semibold hover:underline">{t("nav.login")}</Link>
        </p>
      </Card>
    </div>
  );
}
